import { Link } from "react-router-dom";
import { ShieldCheck, FlaskConical, Landmark, ScrollText } from "lucide-react";

export default function AboutPage() {
    return (
        <div data-testid="about-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-5xl mx-auto px-6 lg:px-10">
                <header className="mb-14">
                    <div className="data-label mb-3">ABOUT</div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
                        Reference-grade research<br />compounds, honestly labeled.
                    </h1>
                    <p className="text-lg text-secondary leading-relaxed max-w-3xl">
                        Aminoplex is a US-based supplier of research-use-only compounds serving qualified academic,
                        industrial, and independent laboratories. We publish a third-party Certificate of Analysis for
                        every batch, we do not make health claims, and we do not sell to consumers.
                    </p>
                </header>

                <section className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
                    <InfoTile icon={FlaskConical} tile="tile-cream" title="Third-party tested">
                        Every lot is analyzed by an independent laboratory (HPLC + MS, with LAL endotoxin screening
                        where applicable). We publish the full COA — not just a purity number — so buyers can verify
                        identity, purity, and lot traceability before ordering.
                    </InfoTile>
                    <InfoTile icon={ShieldCheck} tile="tile-blue" title="Qualified buyers only">
                        We refuse consumer sales and gate every account behind a Research-Use-Only attestation,
                        organization name, and buyer category. Orders associated with pharmacies,
                        med spas, TRT/hormone clinics, weight-loss clinics, gyms, or dormitories are refused and refunded.
                    </InfoTile>
                    <InfoTile icon={Landmark} tile="tile-pink" title="Real US business">
                        Aminoplex is a d/b/a of JNA Ventures LLC, a Virginia-registered limited liability company with a
                        US business bank account, EIN, and traceable ownership. We are not a drop-shipper and we do not
                        white-label third-party inventory.
                    </InfoTile>
                    <InfoTile icon={ScrollText} tile="tile-white" title="Documented compliance">
                        Every page carries the RUO disclosure. Purchasers electronically sign our Terms of Sale before
                        checkout is enabled. Product literature contains no dosage, no protocol, no human-use language,
                        and no claim of therapeutic effect.
                    </InfoTile>
                </section>

                <section className="card p-8 lg:p-10 mb-10">
                    <div className="data-label mb-3">LEGAL &amp; OPERATIONAL</div>
                    <h2 className="font-display text-3xl mb-6">Business information</h2>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 font-mono text-sm">
                        <Detail label="Registered entity" value="JNA Ventures LLC (Virginia)" />
                        <Detail label="Doing business as" value="Aminoplex" />
                        <Detail label="Principal address" value={<>P.O. Box 491<br />Haymarket, VA 20168, United States</>} />
                        <Detail label="Support email" value="support@aminoplex.bio" />
                        <Detail label="Business hours" value="Monday – Friday · 9 AM – 6 PM Eastern" />
                        <Detail label="Order fulfillment" value="Domestic US · same-day dispatch after payment clears" />
                    </dl>
                </section>

                <section className="text-center">
                    <h2 className="font-display text-3xl mb-4">Ready to review our documentation?</h2>
                    <p className="text-secondary max-w-xl mx-auto mb-8">
                        Our full COA library, terms of sale, refund policy, and shipping policy are open to the public.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link to="/coa" className="btn btn-solid" data-testid="about-cta-coa">Certificate of Analysis Library</Link>
                        <Link to="/terms" className="btn" data-testid="about-cta-terms">Terms of Sale</Link>
                        <Link to="/contact" className="btn" data-testid="about-cta-contact">Contact Us</Link>
                    </div>
                </section>
            </div>
        </div>
    );
}

function InfoTile({ icon: Icon, tile, title, children }) {
    return (
        <div className={`rounded-[22px] p-7 ${tile}`}>
            <Icon size={20} className="mb-4" />
            <div className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">{title}</div>
            <p className="text-sm text-secondary leading-relaxed">{children}</p>
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <dt className="data-label mb-1">{label}</dt>
            <dd className="text-primary leading-relaxed">{value}</dd>
        </div>
    );
}
